import { Component, h, Ref } from "preact";
import Uploader from "../../uploader";
import {
  IEdges,
  IImageMeta,
  ILabelOptionValue,
  IOption,
  IReactConfiguration,
} from "../../../typings";
import OutputController from "../../../shared/controller/output-controller";
import TemplateController from "../../../shared/controller/template-controller";
import EventController from "../../../shared/controller/event-controller";
import UnitSystemController, {
  UnitSystem,
} from "../../../shared/controller/unit-system-controller";
import SizeController, {
  ISize,
} from "../../../shared/controller/size-controller";
import ImageRenderer from "./ImageRenderer";

export default class ImageViewer extends Component<
  IImageViewerProps,
  IImageViewerState
> {
  private image: HTMLImageElement;
  private table: HTMLTableElement;
  private timeout: number;

  constructor(props: IImageViewerProps) {
    super(props);

    this.state = {
      image: this.props.conf.asset_prefix + "assets/img/placeholder.svg",
      labelSize: null,
      labelSizeReference: "us-quarter",
      labelSizeReferenceMultiplier: 24.26,
      labelSizeReferenceHeight: 100,
      fullscreen: false,
      edges: {
        top: false,
        right: false,
        bottom: false,
        left: false,
      },
      labelSizeValueFocus: false,
      sizeReferenceValueFirstChange: false,
      maxPreviewWidth: 0,
    };

    this.loadEventListeners = this.loadEventListeners.bind(this);
    this.updateSizeReferenceValue = this.updateSizeReferenceValue.bind(this);
  }

  componentDidMount(): void {
    const image: string = this.props.outputController.getValue([
      "image",
      "editor-preview",
    ]);
    if (image) {
      this.setState({
        image,
      });
    }

    const labelSizeReference: string = this.props.outputController.getValue([
      "label",
      "size_reference",
    ]);
    if (labelSizeReference) {
      this.setState({
        labelSizeReference,
      });
    }

    const labeltype: string = this.props.outputController.getValue([
      "label",
      "labeltype",
    ]);
    if (labeltype) {
      this.setState({
        labeltype,
      });
    }

    const labelSize: ISize = this.props.sizeController.getSize();
    const unitSystem: UnitSystem = this.props.outputController.getValue([
      "system",
      "unit-system",
    ]);

    this.setState({
      labelSize,
      unitSystem,
    });

    const edges: IEdges = this.props.outputController.getValue([
      "label",
      "edges",
    ]);
    if (edges) {
      this.setState({ edges });
    }

    this.loadEventListeners();

    document.documentElement.style.setProperty(
      "--uploader-canvas-image-url",
      "url('" +
        this.props.conf.asset_prefix +
        "assets/img/preview-canvas-grid.png')",
    );

    document
      .querySelector(this.props.conf.canvas_target)
      .parentElement.classList.add("bg-canvas");
  }

  componentWillUnmount(): void {
    document
      .querySelector(this.props.conf.canvas_target)
      .parentElement.classList.remove("bg-canvas");
  }

  loadEventListeners(): void {
    let eventName: string = EventController.getSaveEventName([
      "image",
      "editor-preview",
    ]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const image: string = this.props.outputController.getValue([
          "image",
          "editor-preview",
        ]);
        this.setState({
          image,
        });
      });

    eventName = EventController.getSaveEventName(["system", "unit-system"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const unitSystem: UnitSystem = this.props.outputController.getValue([
          "system",
          "unit-system",
        ]);
        this.setState({
          unitSystem,
        });
      });

    eventName = EventController.getSaveEventName(["label", "labeltype"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const labeltype: string = this.props.outputController.getValue([
          "label",
          "labeltype",
        ]);
        this.setState({
          labeltype,
        });
      });

    eventName = EventController.getSaveEventName(["size", "width"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const size: ISize = this.props.sizeController.getSize();
        this.setState(
          {
            labelSize: size,
          },
          this.updateSizeReferenceValue,
        );
      });

    eventName = EventController.getSaveEventName(["size", "height"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const size: ISize = this.props.sizeController.getSize();
        this.setState(
          {
            labelSize: size,
          },
          this.updateSizeReferenceValue,
        );
      });

    eventName = EventController.getSaveEventName(["label", "edges"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const edges: IEdges = this.props.outputController.getValue([
          "label",
          "edges",
        ]);
        if (edges) {
          this.setState({ edges });
        }
        this.updateSizeReferenceValue();
      });

    eventName = EventController.getSaveEventName(["label", "size_reference"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const labelSizeReference: string = this.props.outputController.getValue(
          ["label", "size_reference"],
        );
        this.setState({
          labelSizeReference,
          labelSizeReferenceMultiplier:
            labelSizeReference === "eu-20-cent" ? 22.25 : 24.26,
        });
      });

    eventName = EventController.getSaveEventName(["label", "size-value"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, this.updateSizeReferenceValue);

    eventName = "size-value_focus";
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        this.setState({
          sizeReferenceValueFirstChange: true,
          labelSizeValueFocus: true,
        });
      });

    eventName = "size-value_blur";
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        this.setState({
          labelSizeValueFocus: false,
        });
      });
  }

  private updateSizeReferenceValue(): void {
    if (typeof this.image === "undefined") {
      return;
    }
    const labelSizeReferenceHeight: number =
      this.image.width / this.getTotalWidth();
    this.setState({
      labelSizeReferenceHeight,
      labelSizeValueFocus: true,
    });
  }

  private imageIsSupported(): boolean {
    return true;
  }

  private getTotalWidth(): number {
    return this.state.labelSize.width;
  }

  private getTotalHeight(): number {
    return this.state.labelSize.height;
  }

  private getLabelTypeName(): string {
    const option: IOption =
      this.props.templateController.getOptionByCode("labeltype");

    const value: ILabelOptionValue =
      this.props.templateController.getOptionValue(
        option,
        this.state.labeltype,
      );

    if (!value) {
      return this.state.labeltype;
    }
    const translatedName: string = value.meta.name;
    if (translatedName === null) {
      return value.meta.name;
    }

    return translatedName;
  }

  getSizeReferenceAnimation(): string {
    if (!this.state.sizeReferenceValueFirstChange) {
      return "pre-animate-size-reference";
    }
    if (this.state.labelSizeValueFocus) {
      return "animate-size-reference-fade-in";
    }
    return "animate-size-reference-fade-out";
  }

  renderPattern(): boolean {
    if (
      this.props.templateController.getTemplate().attributes &&
      this.props.templateController.getTemplate().attributes.fabric &&
      this.props.templateController.getTemplate().attributes.fabric.value
    ) {
      return true;
    }

    return false;
  }

  render(): JSX.Element {
    return (
      <div>
        <div className="grid grid-cols-[2rem_auto_2rem] grid-rows-[2rem_auto] p-6">
          {this.state.labelSize && (
            <div
              className={
                "col-span-3 text-lg text-gray-dark text-center flex justify-center items-baseline"
              }
            >
              {this.props.unitSystemController.convertUnitWithSystem(
                this.getTotalWidth(),
              )}
            </div>
          )}
          <div className={"col-span-1 row-span-1"} />

          <div
            className="image-wrapper"
            ref={(ref: HTMLDivElement) => {
              if (
                ref === null ||
                this.state.maxPreviewWidth === ref.offsetWidth
              ) {
                return;
              }
              this.setState({ maxPreviewWidth: ref.offsetWidth });
            }}
          >
            {this.imageIsSupported() && (
              <ImageRenderer
                canvasRef={this.props.canvasRef}
                conf={this.props.conf}
                outputController={this.props.outputController}
                sizeController={this.props.sizeController}
                externalController={this.props.main.externalController}
                templateController={this.props.main.templateController}
                height={this.props.maxHeight}
                renderFoldLines
                renderCenterFoldLines
                renderSewingAllowanceLines
                originalName={this.props.originalName}
                originalPngName={this.props.originalPngName}
                originalMetaName={this.props.originalMetaName}
                croppedDataName={this.props.croppedDataName}
                renderPattern={this.renderPattern()}
                renderEdgeSkew
                renderCordHole
              />
            )}
          </div>
          {this.state.labelSize && (
            <div className="flex items-end justify-center text-lg text-center vertical-lr text-gray-dark">
              {this.props.unitSystemController.convertUnitWithSystem(
                this.getTotalHeight(),
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

interface IImageViewerProps {
  conf: IReactConfiguration;
  main: Uploader;

  templateLoaded: boolean;

  eventController: EventController;
  outputController: OutputController;
  sizeController: SizeController;
  templateController: TemplateController;
  unitSystemController: UnitSystemController;

  originalName: string;
  originalMetaName: string;
  originalPngName: string;
  croppedDataName: string;
  maxHeight: number;
  canvasRef: Ref<HTMLCanvasElement>;

  option: IOption;
}

interface IImageViewerState {
  image: string;
  unitSystem: UnitSystem;
  labeltype: string;
  labelSize: ISize;
  labelSizeReference: string;
  labelSizeReferenceMultiplier: number;
  labelSizeReferenceHeight: number;
  maxWidthPreviewWrapper: number;
  fullscreen: boolean;
  edges: IEdges;
  sizeReferenceValueFirstChange: boolean;
  labelSizeValueFocus: boolean;
}
